import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardTitle, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather'; // Import all icons
import {
  dispositionGet,
  hourlyGraphGet,
  inboundStatusGet,
  listStatusGet,
  outboundStatusGet,
  topFiveUserGet,
  useDispositionDetails,
  useHourlyGraphDetails,
  useInboundStatusDetails,
  useListStatusDetails,
  useOutboundStatusDetails,
  userStatusGet,
  useTopFiveUserDetails,
  useUerStatus
} from '../../../store/slices/tenantSlices/tenantDashboardSlice';
import ChartComponent from '../../../components/charts/ChartComponent';
import AdminHeader from '../../../components/header/AdminHeader';
import FormSelect from '../../../components/form/FormSelect';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import { userOptionGetByProcessId } from '../../../store/slices/callingSlice';

const TenantDashboard = () => {
  const dispatch = useDispatch();

  const hourlyGraphDetails = useHourlyGraphDetails();
  const topFiveUserDetails = useTopFiveUserDetails();
  const dispositionDetails = useDispositionDetails();
  const outboundStatusDetails = useOutboundStatusDetails();
  const inboundStatusDetails = useInboundStatusDetails();
  const listStatusDetails = useListStatusDetails();
  const userStatus = useUerStatus();

  const [userOptions, setUserOptions] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [processId, setProcessId] = useState('');

  const [hourlyUserId, setHourlyUserId] = useState('');
  const [dispoUserId, setDispoUserId] = useState('');
  const [outboundUserId, setOutboundUserId] = useState('');
  const [inboundUserId, setInboundUserId] = useState('');
  const [hours, setHours] = useState(hourlyGraphDetails?.map((item) => item.hour));
  const [recordCounts, setRecordCounts] = useState(
    hourlyGraphDetails?.map((item) => parseInt(item.record_count))
  );

  const [usernames, setUsernames] = useState(topFiveUserDetails?.map((item) => item.username));
  const [totalCalls, setTotalCalls] = useState(
    topFiveUserDetails?.map((item) => parseInt(item?.total_calls))
  );

  const [dispositionLabels, setDispositionLabels] = useState(
    dispositionDetails?.map((item) => item.disposition || 'Unknown')
  );
  const [dispositionSeries, setDispositionSeries] = useState(
    dispositionDetails?.map((item) => parseInt(item.record_count))
  );

  const [listNames, setListNames] = useState(listStatusDetails?.map((item) => item.list_name));
  const [pendingListCounts, setPendingListCounts] = useState(
    listStatusDetails?.map((item) => parseInt(item.pending_list_count))
  );
  const [dailedListCounts, setDailedListCounts] = useState(
    listStatusDetails?.map((item) => parseInt(item.dailed_list_count))
  );

  const [totalOutboundCalls, setTotalOutboundCalls] = useState(0);
  const [outboundAnsweredCalls, setOutboundAnsweredCalls] = useState(0);
  const [outboundNotAnsweredCalls, setOutboundNotAnsweredCalls] = useState(0);

  const [totalInboundCalls, setTotalInboundCalls] = useState(0);
  const [inboundAnsweredCalls, setInboundAnsweredCalls] = useState(0);
  const [inboundNotAnsweredCalls, setInboundNotAnsweredCalls] = useState(0);

  const [outboundAnsweredPercentage, setOutboundAnsweredPercentage] = useState(0);
  const [outboundNotAnsweredPercentage, setOutboundNotAnsweredPercentage] = useState(0);
  const [inboundAnsweredPercentage, setInboundAnsweredPercentage] = useState(0);
  const [inboundNotAnsweredPercentage, setInboundNotAnsweredPercentage] = useState(0);

  const fetchHourlyGraphDetails = async () => {
    try {
      const payload = { user_id: hourlyUserId || undefined, process_id: processId || undefined };
      await dispatch(hourlyGraphGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchTopFiveDetails = async () => {
    try {
      const payload = { process_id: processId || undefined };
      await dispatch(topFiveUserGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchDispositionDetails = async () => {
    try {
      const payload = { user_id: dispoUserId || undefined, process_id: processId || undefined };
      await dispatch(dispositionGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchOutboundStatusDetails = async () => {
    try {
      const payload = { user_id: outboundUserId || undefined, process_id: processId || undefined };
      await dispatch(outboundStatusGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchInboundStatusDetails = async () => {
    try {
      const payload = { user_id: inboundUserId || undefined, process_id: processId || undefined };
      await dispatch(inboundStatusGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchListStatusDetails = async () => {
    try {
      const payload = { process_id: processId || undefined };
      await dispatch(listStatusGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchUserStatusDetails = async () => {
    try {
      const payload = { process_id: processId || undefined };
      await dispatch(userStatusGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchUserOptionByProcessId = async () => {
    try {
      const response = await dispatch(userOptionGetByProcessId({ process_id: processId })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.error('Error fetching user options:', error);
    }
  };

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchHourlyGraphDetails();
    const interval = setInterval(() => {
      fetchHourlyGraphDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [hourlyUserId, processId]);

  useEffect(() => {
    fetchUserStatusDetails();
    const interval = setInterval(() => {
      fetchUserStatusDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [processId]);

  useEffect(() => {
    fetchDispositionDetails();
    const interval = setInterval(() => {
      fetchDispositionDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [dispoUserId, processId]);

  useEffect(() => {
    fetchOutboundStatusDetails();
    const interval = setInterval(() => {
      fetchOutboundStatusDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [outboundUserId, processId]);

  useEffect(() => {
    fetchInboundStatusDetails();
    const interval = setInterval(() => {
      fetchInboundStatusDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [inboundUserId, processId]);

  useEffect(() => {
    fetchListStatusDetails();
    const interval = setInterval(() => {
      fetchListStatusDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [processId]);

  useEffect(() => {
    fetchTopFiveDetails();
    const interval = setInterval(() => {
      fetchTopFiveDetails();
    }, 20000);

    return () => clearInterval(interval);
  }, [processId]);

  useEffect(() => {
    getProcessOption();
  }, []);

  useEffect(() => {
    fetchUserOptionByProcessId();
  }, [processId]);

  useEffect(() => {
    setHours(hourlyGraphDetails?.map((item) => item.hour));
    setRecordCounts(hourlyGraphDetails?.map((item) => parseInt(item.record_count)));
  }, [hourlyGraphDetails]);

  useEffect(() => {
    setUsernames(topFiveUserDetails?.map((item) => item.username));
    setTotalCalls(topFiveUserDetails?.map((item) => parseInt(item?.total_calls)));
  }, [topFiveUserDetails]);

  useEffect(() => {
    setDispositionLabels(dispositionDetails?.map((item) => item.disposition || 'Unknown'));
    setDispositionSeries(dispositionDetails?.map((item) => parseInt(item.record_count)));
  }, [dispositionDetails]);

  useEffect(() => {
    setListNames(listStatusDetails?.map((item) => item.list_name));
    setPendingListCounts(listStatusDetails?.map((item) => parseInt(item.pending_list_count)));
    setDailedListCounts(listStatusDetails?.map((item) => parseInt(item.dailed_list_count)));
  }, [listStatusDetails]);

  useEffect(() => {
    setTotalOutboundCalls(
      outboundStatusDetails?.length > 0 ? parseInt(outboundStatusDetails?.[0].total_calls) : 0
    );
    setOutboundAnsweredCalls(
      outboundStatusDetails?.length > 0 ? parseInt(outboundStatusDetails?.[0].answered_calls) : 0
    );
    setOutboundNotAnsweredCalls(
      outboundStatusDetails?.length > 0
        ? parseInt(outboundStatusDetails?.[0].not_answered_calls)
        : 0
    );

    if (totalOutboundCalls > 0) {
      setOutboundAnsweredPercentage((outboundAnsweredCalls / totalOutboundCalls) * 100);
      setOutboundNotAnsweredPercentage((outboundNotAnsweredCalls / totalOutboundCalls) * 100);
    }
  }, [outboundStatusDetails, totalOutboundCalls, outboundAnsweredCalls, outboundNotAnsweredCalls]);

  useEffect(() => {
    setTotalInboundCalls(
      inboundStatusDetails?.length > 0 ? parseInt(inboundStatusDetails?.[0].total_calls) : 0
    );
    setInboundAnsweredCalls(
      inboundStatusDetails?.length > 0 ? parseInt(inboundStatusDetails?.[0].answered_calls) : 0
    );
    setInboundNotAnsweredCalls(
      inboundStatusDetails?.length > 0 ? parseInt(inboundStatusDetails?.[0].not_answered_calls) : 0
    );

    if (totalInboundCalls > 0) {
      setInboundAnsweredPercentage((inboundAnsweredCalls / totalInboundCalls) * 100);
      setInboundNotAnsweredPercentage((inboundNotAnsweredCalls / totalInboundCalls) * 100);
    }
  }, [inboundStatusDetails, totalInboundCalls, inboundAnsweredCalls, inboundNotAnsweredCalls]);

  const hourlySeries = useMemo(
    () => [
      {
        name: 'Call Count',
        data: recordCounts
      }
    ],
    [recordCounts]
  );

  const hourlyOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        height: 200,
        toolbar: {
          show: false
        }
      },
      xaxis: {
        categories: hours
      },
      stroke: {
        curve: 'smooth'
      },
      fill: {
        opacity: 0.4
      },
      dataLabels: {
        enabled: false
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    }),
    [hours]
  );

  const userSeries = useMemo(() => totalCalls, [totalCalls]);

  const userChartOptions = useMemo(
    () => ({
      chart: {
        type: 'polarArea',
        height: 200
      },
      labels: usernames,
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }
      ],
      fill: {
        opacity: 0.7
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const originalCount = opts.w.config.series[opts.seriesIndex];
          return `${originalCount} calls`;
        }
      },
      tooltip: {
        y: {
          formatter: (value) => `${value} calls`
        }
      }
    }),
    [usernames]
  );

  const dispositionOptions = useMemo(
    () => ({
      chart: {
        type: 'pie',
        height: 200
      },
      labels: dispositionLabels,
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }
      ],
      fill: {
        opacity: 1
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const originalCount = opts.w.config.series[opts.seriesIndex];
          return `${originalCount}`;
        }
      },
      tooltip: {
        y: {
          formatter: (value) => `${value} calls`
        }
      }
    }),
    [dispositionLabels]
  );

  const outboundCallSeries = useMemo(
    () => [
      totalOutboundCalls > 0 ? 100 : 0,
      outboundAnsweredPercentage,
      outboundNotAnsweredPercentage
    ],
    [outboundAnsweredPercentage, outboundNotAnsweredPercentage]
  );
  const outboundActualData = useMemo(
    () => [totalOutboundCalls, outboundAnsweredCalls, outboundNotAnsweredCalls],
    [outboundAnsweredCalls, outboundNotAnsweredCalls]
  );

  const outboundCallOptions = useMemo(
    () => ({
      plotOptions: {
        radialBar: {
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '50%'
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              show: false
            }
          },
          barLabels: {
            enabled: true,
            useSeriesColors: true,
            offsetX: -8,
            fontSize: '12px',
            formatter: function (seriesName, opts) {
              const actualCount = opts.w.config.userData?.[opts.seriesIndex];
              return `${seriesName}: ${actualCount}`;
            }
          }
        }
      },
      labels: ['Total Calls', 'Answered Calls', 'Not Answered Calls'],
      userData: outboundActualData,
      fill: {
        opacity: 1
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300
            },
            legend: {
              show: false
            }
          }
        }
      ]
    }),
    [outboundActualData]
  );

  const inboundCallSeries = useMemo(
    () => [
      totalInboundCalls > 0 ? 100 : 0,
      inboundAnsweredPercentage,
      inboundNotAnsweredPercentage
    ],
    [inboundAnsweredPercentage, inboundNotAnsweredPercentage]
  );
  const inboundActualData = useMemo(
    () => [totalInboundCalls, inboundAnsweredCalls, inboundNotAnsweredCalls],
    [inboundAnsweredCalls, inboundNotAnsweredCalls]
  );

  const inboundCallOptions = useMemo(
    () => ({
      plotOptions: {
        radialBar: {
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '50%'
          },
          dataLabels: {
            name: {
              show: false
            },
            value: {
              show: false
            }
          },
          barLabels: {
            enabled: true,
            useSeriesColors: true,
            offsetX: -8,
            fontSize: '12px',
            formatter: function (seriesName, opts) {
              const actualCount = opts.w.config.userData?.[opts.seriesIndex];
              return `${seriesName}: ${actualCount}`;
            }
          }
        }
      },
      labels: ['Total Calls', 'Answered Calls', 'Not Answered Calls'],
      userData: inboundActualData,
      fill: {
        opacity: 1
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 300
            },
            legend: {
              show: false
            }
          }
        }
      ]
    }),
    [inboundActualData]
  );

  const listSeries = useMemo(
    () => [
      {
        name: 'Pending List Count',
        data: pendingListCounts
      },
      {
        name: 'Dailed List Count',
        data: dailedListCounts
      }
    ],
    [pendingListCounts, dailedListCounts]
  );

  const listOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 350,
        stacked: true
      },
      plotOptions: {
        bar: {
          horizontal: true
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          return val;
        }
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: listNames
      },
      fill: {
        opacity: 1
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        floating: true
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    }),
    [listNames]
  );

  return (
    <React.Fragment>
      <AdminHeader
        title="Dashboard"
        isExpandSelect
        selectValue={processId}
        handelSelect={(e) => setProcessId(e.target.value)}
        options={[{ value: '', label: 'All' }, ...processOptions]}
      />
      <Row className="dashboard-card-container">
        <Col lg="3">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #2cabe3`, backgroundColor: `#2cabe333` }}>
            <div className="card-icon" style={{ backgroundColor: `#2cabe3` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.Slash size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Idle</span>
              <span className="fs-3">{userStatus?.idle || 0}</span>
            </div>
          </Card>
        </Col>
        <Col lg="3">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #008000`, backgroundColor: `#00800033` }}>
            <div className="card-icon" style={{ backgroundColor: `#008000` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.PhoneCall size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">On Call</span>
              <span className="fs-3">{userStatus?.on_call || 0}</span>
            </div>
          </Card>
        </Col>
        <Col lg="3">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #ffb22b`, backgroundColor: `#ffb22b33` }}>
            <div className="card-icon" style={{ backgroundColor: `#ffb22b` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.Rss size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Others</span>
              <span className="fs-3">{userStatus?.others || 0}</span>
            </div>
          </Card>
        </Col>
        <Col lg="3">
          <Card
            className="report-card px-2"
            style={{ borderBottom: `2px solid #122335`, backgroundColor: `#12233533` }}>
            <div className="card-icon" style={{ backgroundColor: `#122335` }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.Phone size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Total</span>
              <span className="fs-3">{userStatus?.total_users || 0}</span>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg="8">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Call Count Over Hours"
            height={245}
            series={hourlySeries}
            options={hourlyOptions}
            chartType="area"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Call Count Over Hours</CardTitle>
                <FormSelect
                  isShowLabel={false}
                  className="w-25 mb-0"
                  name="hourlyUserId"
                  placeholder="Select User"
                  value={hourlyUserId}
                  options={[{ value: '', label: 'All' }, ...userOptions]}
                  onChange={(e) => setHourlyUserId(e.target.value)}
                />
              </div>
            }
          />
        </Col>
        <Col lg="4">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Top Five Users"
            height={258}
            series={userSeries}
            options={userChartOptions}
            chartType="polarArea"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Top Five Users</CardTitle>
              </div>
            }
          />
        </Col>
      </Row>
      <Row className="pt-3">
        <Col lg="4">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Disposition"
            height={245}
            series={dispositionSeries}
            options={dispositionOptions}
            chartType="pie"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Disposition</CardTitle>
                <FormSelect
                  isShowLabel={false}
                  className="w-50 mb-0"
                  name="dispoUserId"
                  placeholder="Select User"
                  value={dispoUserId}
                  options={[{ value: '', label: 'All' }, ...userOptions]}
                  onChange={(e) => setDispoUserId(e.target.value)}
                />
              </div>
            }
          />
        </Col>
        <Col lg="4">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Outbound Call Count"
            height={245}
            series={outboundCallSeries}
            options={outboundCallOptions}
            chartType="radialBar"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Outbound Call Count</CardTitle>
                <FormSelect
                  isShowLabel={false}
                  className="w-50 mb-0"
                  name="outboundUserId"
                  placeholder="Select User"
                  value={outboundUserId}
                  options={[{ value: '', label: 'All' }, ...userOptions]}
                  onChange={(e) => setOutboundUserId(e.target.value)}
                />
              </div>
            }
          />
        </Col>
        <Col lg="4">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="Inbound Call Count"
            height={245}
            series={inboundCallSeries}
            options={inboundCallOptions}
            chartType="radialBar"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">Inbound Call Count</CardTitle>
                <FormSelect
                  isShowLabel={false}
                  className="w-50 mb-0"
                  name="inboundUserId"
                  placeholder="Select User"
                  value={inboundUserId}
                  options={[{ value: '', label: 'All' }, ...userOptions]}
                  onChange={(e) => setInboundUserId(e.target.value)}
                />
              </div>
            }
          />
        </Col>
      </Row>
      <Row className="pt-3">
        <Col lg="12">
          <ChartComponent
            cardClass="mb-0"
            bodyClass="px-3 pt-3 pb-0"
            title="List Status"
            height={245}
            series={listSeries}
            options={listOptions}
            chartType="bar"
            chartHeader={
              <div className="d-flex align-items-baseline justify-content-between">
                <CardTitle tag="h5">List Status</CardTitle>
              </div>
            }
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default TenantDashboard;
